/**
 * The route tree.
 *
 * This replaces Next.js file-based routing. The `(app)` route group became a parent route whose
 * element is AppLayout, and the 51 screens that sat inside it are now its children. `[assetId]` and
 * the rest became `:assetId`.
 *
 * The four routes OUTSIDE the shell are the same four that sat outside the route group: the landing
 * page, sign-in, the welcome tour and the PIN reset. UF_00 section 4 is the reason, and it is not
 * cosmetic: a person who is not signed in has no tier badge to show, no notifications to open and no
 * wallet to navigate to, so rendering the five-tab chrome around them would be a promise the app
 * cannot keep.
 *
 * Static children outrank dynamic siblings by specificity here, so /tokenize/new resolves to the
 * submission form rather than to an asset whose id is the word new. Under file-based routing the
 * directory layout made that promise; the router makes it now, and it does not depend on the order
 * of this list.
 */

import { createBrowserRouter } from 'react-router-dom';

import { AppLayout } from './AppLayout';
import { Root } from './Root';
import { RouteError } from './RouteError';
import { AppShell } from '@/components/shell';
import ActivityCorrelationId from '@/routes/ActivityCorrelationId';
import Business from '@/routes/Business';
import Credit from '@/routes/Credit';
import CreditApply from '@/routes/CreditApply';
import CreditBorrow from '@/routes/CreditBorrow';
import CreditFinance from '@/routes/CreditFinance';
import CreditFinanceId from '@/routes/CreditFinanceId';
import CreditRequestsId from '@/routes/CreditRequestsId';
import CreditCollateral from '@/routes/CreditCollateral';
import CreditLoans from '@/routes/CreditLoans';
import CreditLoansId from '@/routes/CreditLoansId';
import CreditMarketplace from '@/routes/CreditMarketplace';
import CreditOffers from '@/routes/CreditOffers';
import CreditOffersId from '@/routes/CreditOffersId';
import CreditPassportAssetId from '@/routes/CreditPassportAssetId';
import CreditRepay from '@/routes/CreditRepay';
import Disclosures from '@/routes/Disclosures';
import Earnings from '@/routes/Earnings';
import Home from '@/routes/Home';
import Landing from '@/routes/Landing';
import Lender from '@/routes/Lender';
import Market from '@/routes/Market';
import Marketplace from '@/routes/Marketplace';
import MarketplaceId from '@/routes/MarketplaceId';
import Merchant from '@/routes/Merchant';
import Notifications from '@/routes/Notifications';
import P2p from '@/routes/P2p';
import Swap from '@/routes/Swap';
import P2pTrade from '@/routes/P2pTrade';
import P2pTrades from '@/routes/P2pTrades';
import P2pOfferNew from '@/routes/P2pOfferNew';
import PayScan from '@/routes/PayScan';
import Portfolio from '@/routes/Portfolio';
import PortfolioAnalytics from '@/routes/PortfolioAnalytics';
import PortfolioTax from '@/routes/PortfolioTax';
import Profile from '@/routes/Profile';
import Receive from '@/routes/Receive';
import ResetPin from '@/routes/ResetPin';
import Settings from '@/routes/Settings';
import SettingsAccounts from '@/routes/SettingsAccounts';
import SettingsData from '@/routes/SettingsData';
import Signin from '@/routes/Signin';
import Support from '@/routes/Support';
import SupportCaseId from '@/routes/SupportCaseId';
import Till from '@/routes/Till';
import Tokenize from '@/routes/Tokenize';
import TokenizeApplicationsId from '@/routes/TokenizeApplicationsId';
import TokenizeAssetsAssetId from '@/routes/TokenizeAssetsAssetId';
import TokenizeAssetsAssetIdOfferingsNew from '@/routes/TokenizeAssetsAssetIdOfferingsNew';
import TokenizeDistributions from '@/routes/TokenizeDistributions';
import TokenizeNew from '@/routes/TokenizeNew';
import TokenizeOfferings from '@/routes/TokenizeOfferings';
import TokenizePortfolio from '@/routes/TokenizePortfolio';
import TokenizeTokensTokenId from '@/routes/TokenizeTokensTokenId';
import TokenizeTokensTokenIdList from '@/routes/TokenizeTokensTokenIdList';
import Transact from '@/routes/Transact';
import Trust from '@/routes/Trust';
import TrustId from '@/routes/TrustId';
import TrustNew from '@/routes/TrustNew';
import VerifyTier1 from '@/routes/VerifyTier1';
import VerifyTier2 from '@/routes/VerifyTier2';
import Wallet from '@/routes/Wallet';
import WalletAssetCode from '@/routes/WalletAssetCode';
import WalletDeposit from '@/routes/WalletDeposit';
import WalletWithdraw from '@/routes/WalletWithdraw';
import Watchlist from '@/routes/Watchlist';
import Welcome from '@/routes/Welcome';

export const router = createBrowserRouter([
  {
    // The provider stack is a route element, not a wrapper around RouterProvider. See Root.tsx: the
    // session provider navigates, and useNavigate() only works beneath the router.
    element: <Root />,
    /**
     * THE OUTER BOUNDARY. Catches anything thrown outside the signed-in shell, and anything the
     * inner one cannot (a failure in AppLayout itself). Without an errorElement anywhere, React
     * Router renders its own developer page -- stack trace and all -- to a participant.
     */
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Landing /> },
      { path: '/reset-pin', element: <ResetPin /> },
      { path: '/signin', element: <Signin /> },
      { path: '/welcome', element: <Welcome /> },
      {
        element: <AppLayout />,
        /**
         * THE INNER BOUNDARY, INSIDE THE CHROME. A screen that throws should not also strip the
         * navigation away: somebody whose withdraw screen just failed needs one tap back to their
         * wallet, not a dead end. It renders inside AppShell rather than replacing it, and the
         * session providers above it are untouched because they live on the parent route.
         */
        errorElement: (
          <AppShell>
            <RouteError />
          </AppShell>
        ),
        children: [
          { path: 'activity/:correlationId', element: <ActivityCorrelationId /> },
          { path: 'business', element: <Business /> },
          { path: 'credit', element: <Credit /> },
          { path: 'credit/apply', element: <CreditApply /> },
          // THE TWO DOORS. `credit/borrow` is the product marketplace; `credit/finance` is the
          // financier's inbox. Both operate on the same requests, which is why they are siblings
          // under `credit` rather than a separate section of the app.
          { path: 'credit/borrow', element: <CreditBorrow /> },
          { path: 'credit/finance', element: <CreditFinance /> },
          { path: 'credit/finance/:id', element: <CreditFinanceId /> },
          { path: 'credit/requests/:id', element: <CreditRequestsId /> },
          { path: 'credit/collateral', element: <CreditCollateral /> },
          { path: 'credit/loans', element: <CreditLoans /> },
          { path: 'credit/loans/:id', element: <CreditLoansId /> },
          { path: 'credit/marketplace', element: <CreditMarketplace /> },
          { path: 'credit/offers', element: <CreditOffers /> },
          { path: 'credit/offers/:id', element: <CreditOffersId /> },
          { path: 'credit/passport/:assetId', element: <CreditPassportAssetId /> },
          { path: 'credit/repay', element: <CreditRepay /> },
          { path: 'disclosures', element: <Disclosures /> },
          { path: 'earnings', element: <Earnings /> },
          { path: 'home', element: <Home /> },
          { path: 'lender', element: <Lender /> },
          { path: 'market', element: <Market /> },
          { path: 'marketplace', element: <Marketplace /> },
          { path: 'marketplace/:id', element: <MarketplaceId /> },
          { path: 'merchant', element: <Merchant /> },
          { path: 'notifications', element: <Notifications /> },
          { path: 'p2p', element: <P2p /> },
          { path: 'swap', element: <Swap /> },
          { path: 'p2p/offers/new', element: <P2pOfferNew /> },
          { path: 'p2p/trades', element: <P2pTrades /> },
          { path: 'p2p/trades/:tradeId', element: <P2pTrade /> },
          { path: 'pay/scan', element: <PayScan /> },
          { path: 'portfolio', element: <Portfolio /> },
          { path: 'portfolio/analytics', element: <PortfolioAnalytics /> },
          { path: 'portfolio/tax', element: <PortfolioTax /> },
          { path: 'profile', element: <Profile /> },
          { path: 'receive', element: <Receive /> },
          { path: 'settings', element: <Settings /> },
          { path: 'settings/accounts', element: <SettingsAccounts /> },
          { path: 'settings/data', element: <SettingsData /> },
          { path: 'support', element: <Support /> },
          { path: 'support/:caseId', element: <SupportCaseId /> },
          { path: 'till', element: <Till /> },
          { path: 'tokenize', element: <Tokenize /> },
          { path: 'tokenize/applications/:applicationId', element: <TokenizeApplicationsId /> },
          { path: 'tokenize/portfolio', element: <TokenizePortfolio /> },
          { path: 'tokenize/assets/:assetId', element: <TokenizeAssetsAssetId /> },
          {
            path: 'tokenize/assets/:assetId/offerings/new',
            element: <TokenizeAssetsAssetIdOfferingsNew />,
          },
          { path: 'tokenize/distributions', element: <TokenizeDistributions /> },
          { path: 'tokenize/new', element: <TokenizeNew /> },
          { path: 'tokenize/offerings', element: <TokenizeOfferings /> },
          { path: 'tokenize/tokens/:tokenId', element: <TokenizeTokensTokenId /> },
          { path: 'tokenize/tokens/:tokenId/list', element: <TokenizeTokensTokenIdList /> },
          { path: 'transact', element: <Transact /> },
          { path: 'trust', element: <Trust /> },
          { path: 'trust/:id', element: <TrustId /> },
          { path: 'trust/new', element: <TrustNew /> },
          { path: 'verify/tier1', element: <VerifyTier1 /> },
          { path: 'verify/tier2', element: <VerifyTier2 /> },
          { path: 'wallet', element: <Wallet /> },
          { path: 'wallet/asset/:code', element: <WalletAssetCode /> },
          { path: 'wallet/deposit', element: <WalletDeposit /> },
          { path: 'wallet/withdraw', element: <WalletWithdraw /> },
          { path: 'watchlist', element: <Watchlist /> },
        ],
      },
    ],
  },
]);
